import React from "react";
import { Card, Flex, QueryLoader, Text } from "@jsq/ds/core";
import { STATUS_CONFIG } from "./constants";
import { DonutChart, type DonutSegment } from "./DonutChart";
import { DonutLegendItem } from "./DonutLegendItem";
import { cardBody, cardSubtitle, donutWrapper, legendWrapper } from "./styles_vanilla.css";
import { usePositionStatusCounts } from "./usePositionStatusCounts";

type StatusCardBodyProps = {
  arenaId: number;
  entityId?: number | null;
  legendPlacement?: "below" | "right";
};

export const ComplianceStatusCardBody: React.FC<StatusCardBodyProps> = ({
  arenaId,
  entityId,
  legendPlacement = "below",
}) => {
  const { counts, total, queryResults } = usePositionStatusCounts({ arenaId, entityId });

  const visibleStatusConfig = STATUS_CONFIG.filter(
    ({ category }) => !(category === "pendingDivestment" && counts.pendingDivestment === 0),
  );

  const segments: DonutSegment[] = visibleStatusConfig.map(({ category, color, label }) => ({
    value: counts[category],
    color,
    label,
  }));

  return (
    <QueryLoader result={queryResults}>
      <Flex className={cardBody[legendPlacement]}>
        <Flex
          className={donutWrapper[legendPlacement]}
          flexDirection="column"
          alignItems="center"
        >
          <DonutChart segments={segments} totalCount={total} totalLabel="Total positions" />
        </Flex>
        <Flex className={legendWrapper[legendPlacement]} flexDirection="column">
          {visibleStatusConfig.map(({ category, label, color, tooltip }) => (
            <DonutLegendItem
              key={category}
              color={color}
              label={label}
              count={counts[category]}
              tooltip={tooltip}
            />
          ))}
        </Flex>
      </Flex>
    </QueryLoader>
  );
};

export const ComplianceStatusCard: React.FC<
  StatusCardBodyProps & { subtitle?: string | null }
> = ({ arenaId, entityId, subtitle, legendPlacement = "below" }) => (
  <Card
    title="AML/KYC status"
    subTitle={
      <Text variant="regular-md" className={cardSubtitle}>
        {subtitle ?? "--"}
      </Text>
    }
  >
    <ComplianceStatusCardBody
      arenaId={arenaId}
      entityId={entityId}
      legendPlacement={legendPlacement}
    />
  </Card>
);
